import { useRef, useState } from "react";
import { AlertTriangle, CheckCircle2, Download, FileSpreadsheet, Loader2, Upload, X } from "lucide-react";
import { toast } from "sonner";
import { api, API_BASE, formatErr } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

/**
 * ImportDialog for Excel/CSV imports.
 * Props:
 *  - entity: 'materials' | 'products' | 'prices'
 *  - title, description
 *  - onDone: callback after successful commit
 *  - open, onOpenChange
 */
export default function ImportDialog({ entity, title, description, open, onOpenChange, onDone }) {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [committing, setCommitting] = useState(false);

  const reset = () => { setFile(null); setPreview(null); if (fileRef.current) fileRef.current.value = ""; };

  const runPreview = async (f) => {
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post(`/import/${entity}/preview`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data);
    } catch (e) { toast.error(formatErr(e)); setPreview(null); }
    finally { setUploading(false); }
  };

  const onFile = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setFile(f); setPreview(null); runPreview(f);
  };

  const commit = async () => {
    if (!file) return;
    setCommitting(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post(`/import/${entity}/commit?upsert=true`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Berhasil: ${data.inserted} baru, ${data.updated} diperbarui, ${data.errors.length} gagal`);
      onDone?.(data); onOpenChange(false); reset();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setCommitting(false); }
  };

  const close = () => { reset(); onOpenChange(false); };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="max-w-3xl">
        <DialogHeader><DialogTitle className="font-cabinet text-xl">{title}</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-500 -mt-2">{description}</p>

        <div className="flex items-center justify-between rounded-2xl border border-slate-200 bg-slate-50 p-4">
          <div className="flex items-center gap-3">
            <FileSpreadsheet className="w-6 h-6 text-emerald-600" />
            <div>
              <div className="text-sm font-semibold">Unduh template Excel</div>
              <div className="text-xs text-slate-500">Isi kolom sesuai instruksi lalu unggah kembali.</div>
            </div>
          </div>
          <a href={`${API_BASE}/import/${entity}/template.xlsx`} target="_blank" rel="noreferrer" className="gc-btn-ghost text-sm" data-testid={`btn-template-${entity}`}><Download className="w-4 h-4" /> Unduh Template</a>
        </div>

        <label className="mt-2 flex flex-col items-center justify-center rounded-2xl border-2 border-dashed border-slate-300 hover:border-emerald-500 cursor-pointer py-8 transition-colors">
          <Upload className="w-6 h-6 text-slate-400" />
          <div className="mt-2 text-sm font-semibold">Klik untuk pilih file (.xlsx / .csv)</div>
          {file && <div className="mt-1 text-xs text-slate-500">{file.name}</div>}
          <input ref={fileRef} type="file" accept=".xlsx,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv" className="hidden" onChange={onFile} data-testid={`input-file-${entity}`} />
        </label>

        {uploading && <div className="flex items-center gap-2 text-sm text-slate-500"><Loader2 className="w-4 h-4 animate-spin" /> Memeriksa file…</div>}

        {preview && (
          <div className="space-y-3">
            <div className="flex items-center gap-4 flex-wrap text-sm">
              <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600" /><span>{preview.ok} baris siap</span></div>
              {preview.error > 0 && <div className="flex items-center gap-2 text-rose-600"><AlertTriangle className="w-4 h-4" /><span>{preview.error} baris error</span></div>}
              <div className="text-slate-500">Total: {preview.total} baris</div>
            </div>

            <div className="max-h-64 overflow-y-auto rounded-xl border border-slate-200">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-[11px] uppercase tracking-widest text-slate-500 sticky top-0">
                  <tr>
                    <th className="text-left px-3 py-2">Baris</th>
                    <th className="text-left px-3 py-2">Status</th>
                    <th className="text-left px-3 py-2">Data / Error</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.rows.slice(0, 100).map((r) => (
                    <tr key={r.row} className="border-t border-slate-100">
                      <td className="px-3 py-2 font-mono text-xs">{r.row}</td>
                      <td className="px-3 py-2">
                        {r.status === "ok"
                          ? <span className="gc-badge bg-emerald-50 text-emerald-700 border border-emerald-200">OK</span>
                          : <span className="gc-badge bg-rose-50 text-rose-700 border border-rose-200">Error</span>}
                      </td>
                      <td className="px-3 py-2 text-xs">
                        {r.errors.length > 0 ? (
                          <span className="text-rose-700">{r.errors.join("; ")}</span>
                        ) : (
                          <span className="text-slate-600">{Object.entries(r.data).filter(([_, v]) => v !== null && v !== "").slice(0, 4).map(([k, v]) => `${k}: ${v}`).join(" • ")}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
          <button onClick={close} className="gc-btn-ghost"><X className="w-4 h-4" /> Batal</button>
          <button onClick={commit} disabled={!preview || preview.ok === 0 || committing} className="gc-btn-primary" data-testid={`btn-commit-${entity}`}>
            {committing ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Upload className="w-4 h-4" /> Impor {preview?.ok || 0} baris</>}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
